/**
 * Restauration de « l'endroit dans la liste » au retour de la visionneuse.
 *
 * BUT (retour utilisateur) — « quitter le mode galerie doit revenir à l'endroit de la
 * photo dans la liste ». Quand un écran de LISTE ouvre la visionneuse plein écran puis
 * la referme, l'utilisateur doit retomber au MÊME endroit (défilement + état de page)
 * plutôt qu'en haut de la liste. Utile pour TOUTES les listes.
 *
 * Cas concret : la revue de dédoublonnage (`/outils/dedup`). La visionneuse (`/photo/:id`)
 * est rendue sous le layout GALERIE, PAS sous la route dédoublonnage → ouvrir la
 * visionneuse DÉMONTE `DedupReviewPage` ; la refermer la REMONTE à neuf (défilement,
 * page courante et décisions Conserver/Effacer perdus). Ce module offre une réserve
 * (« stash ») persistée en `sessionStorage`, écrite JUSTE avant de naviguer vers la
 * visionneuse et relue UNE FOIS au remontage (puis effacée) → la page se restaure.
 *
 * PRINCIPES
 * - Cœur PUR & testable (`saveStash`/`readStashOnce`) — indépendant de React, injecte
 *   son `Storage` (les tests passent un faux `sessionStorage`).
 * - LECTURE DESTRUCTIVE (« read-once-then-clear ») : relire consomme la réserve. Une
 *   entrée FRAÎCHE sur l'écran (sans réserve) ne restaure donc jamais un état périmé ;
 *   seul un retour de la visionneuse (qui a écrit la réserve) restaure.
 * - Générique : la réserve est `{ scrollTop, ...extra }` où `extra` est un objet
 *   sérialisable JSON propre à l'appelant (page, décisions…). Clé libre par appelant.
 * - Tolérant à un storage indisponible / à un JSON corrompu (jamais d'exception :
 *   mode privé, quotas, ancien format) → au pire, pas de restauration.
 *
 * RÉUTILISATION (Lot H — grille de dossiers) : appeler `saveScrollStash(key, node, extra)`
 * avant d'ouvrir la visionneuse, et `useScrollRestore(key, ref, ready, apply)` au
 * montage de la page pour restaurer défilement + `extra`.
 */

import { useLayoutEffect, useRef, useState } from 'react';

/** Une réserve : la position de défilement + un supplément libre (sérialisable JSON). */
export interface ScrollStash<E> {
  scrollTop: number;
  extra: E;
}

/** Préfixe des clés de réserve dans `sessionStorage` (évite les collisions). */
const KEY_PREFIX = 'xphoto.scrollRestore.';

function storageKey(key: string): string {
  return `${KEY_PREFIX}${key}`;
}

/**
 * Écrit une réserve pour `key`. `extra` DOIT être sérialisable JSON (l'appelant
 * sérialise lui-même toute structure non-JSON, ex. une `Map`). No-op silencieux si le
 * storage est indisponible ou si la sérialisation échoue (jamais d'exception).
 */
export function saveStash<E>(storage: Storage, key: string, stash: ScrollStash<E>): void {
  try {
    storage.setItem(storageKey(key), JSON.stringify(stash));
  } catch {
    // Storage plein / indisponible / valeur non sérialisable : on abandonne la réserve.
  }
}

/**
 * Lit puis EFFACE la réserve de `key` (lecture destructive « read-once »). Retourne
 * `null` s'il n'y a pas de réserve, si le storage est indisponible, ou si le contenu
 * est corrompu/mal formé. La forme `extra` n'est PAS validée ici (l'appelant connaît
 * son type) — on garantit seulement un objet avec un `scrollTop` numérique.
 */
export function readStashOnce<E>(storage: Storage, key: string): ScrollStash<E> | null {
  const k = storageKey(key);
  let raw: string | null;
  try {
    raw = storage.getItem(k);
  } catch {
    return null;
  }
  // Toujours tenter d'effacer (même si le parse échoue → pas de réserve « collante »).
  try {
    storage.removeItem(k);
  } catch {
    // Ignoré : au pire, la réserve sera écrasée à la prochaine sauvegarde.
  }
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'scrollTop' in parsed &&
      typeof (parsed as { scrollTop: unknown }).scrollTop === 'number'
    ) {
      return parsed as ScrollStash<E>;
    }
    return null;
  } catch {
    return null;
  }
}

/** Efface la réserve de `key` sans la lire (ex. reset explicite via le fil d'Ariane). */
export function clearStash(storage: Storage, key: string): void {
  try {
    storage.removeItem(storageKey(key));
  } catch {
    // Storage indisponible : rien à faire.
  }
}

/** Accès défensif au `sessionStorage` du navigateur (null en environnement sans DOM). */
function sessionStorageOrNull(): Storage | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    return null;
  }
}

/**
 * Écrit une réserve `{ scrollTop, extra }` depuis un élément DOM défilant, juste avant
 * de naviguer vers la visionneuse. `node` peut être `null` (ref non encore montée) →
 * `scrollTop` vaut alors 0. Wrapper prêt à l'emploi autour de `saveStash` + le
 * `sessionStorage` du navigateur.
 */
export function saveScrollStash<E>(key: string, node: HTMLElement | null, extra: E): void {
  const storage = sessionStorageOrNull();
  if (!storage) return;
  saveStash<E>(storage, key, { scrollTop: node?.scrollTop ?? 0, extra });
}

/**
 * Lit UNE FOIS (puis efface) la réserve du navigateur pour `key`. Retourne `null` hors
 * navigateur ou en l'absence de réserve.
 */
export function readScrollStashOnce<E>(key: string): ScrollStash<E> | null {
  const storage = sessionStorageOrNull();
  if (!storage) return null;
  return readStashOnce<E>(storage, key);
}

/** Efface la réserve du navigateur pour `key` (sans la lire). */
export function clearScrollStash(key: string): void {
  const storage = sessionStorageOrNull();
  if (!storage) return;
  clearStash(storage, key);
}

/**
 * Hook de restauration au montage d'un écran de LISTE.
 *
 * Au (re)montage, lit UNE FOIS la réserve de `key`. S'il y en a une :
 *  1. applique le supplément `extra` (l'appelant restaure page / décisions… via `apply`,
 *     appelé AVANT la pose du défilement pour que la liste ait la bonne hauteur) ;
 *  2. attend que `ready` passe à `true` (les données de la page restaurée sont chargées
 *     et disposées — la mesure de layout n'existe qu'à ce moment) ;
 *  3. pose `scrollRef.current.scrollTop` dans un `useLayoutEffect` (avant peinture → pas
 *     de saut visible), puis considère la restauration terminée.
 *
 * COURSE « page > 0 » gérée : si `apply` change la page (ex. `setPage(3)`), un rechargement
 * est déclenché. Or, sur le passage d'effet où `apply` vient d'être appelé, `ready` reflète
 * encore l'ANCIENNE page (page 0, souvent déjà `ready`) → poser le défilement là serait
 * prématuré et sur le mauvais contenu. On saute donc TOUJOURS la pose du défilement sur le
 * passage d'application ; le défilement n'est posé qu'à un passage ULTÉRIEUR où `ready`
 * est vrai (données de la page restaurée réellement chargées et disposées).
 *
 * La réserve est consommée (effacée) à la lecture → un remontage ultérieur SANS nouvelle
 * réserve (entrée fraîche depuis ailleurs) ne restaure rien. `apply` n'est invoqué
 * qu'une seule fois (au premier passage où une réserve existe).
 *
 * @param key    Clé de réserve (identique à celle passée à `saveScrollStash`).
 * @param scrollRef Ref de l'élément DOM défilant à repositionner.
 * @param ready  `true` quand la page restaurée est chargée/disposée (mesurable).
 * @param apply  Rappel qui applique le supplément `extra` (page, décisions…). Reçoit
 *               l'`extra` lu ; l'appelant le désérialise (ex. re-crée une `Map`).
 */
export function useScrollRestore<E>(
  key: string,
  scrollRef: React.RefObject<HTMLElement | null>,
  ready: boolean,
  apply: (extra: E) => void,
): void {
  // Réserve en attente de pose du défilement : `undefined` = pas encore lu,
  // `null` = rien à restaurer / restauration terminée, sinon réserve en attente.
  const pending = useRef<ScrollStash<E> | null | undefined>(undefined);
  // Passage d'effet sur lequel `apply` a été appelé (on n'y pose JAMAIS le défilement :
  // `ready` y reflète encore l'état pré-application).
  const justApplied = useRef(false);
  // Garde la dernière closure `apply` sans re-déclencher l'effet (identité stable).
  const applyRef = useRef(apply);
  applyRef.current = apply;
  // Compteur qui force un passage d'effet APRÈS l'application, même si `ready`/`key` ne
  // changent pas (cas page restaurée = 0 : `apply` ne modifie pas `ready`, donc les deps
  // seraient identiques et l'effet ne se re-déclencherait pas sans ce trigger).
  const [tick, setTick] = useState(0);

  useLayoutEffect(() => {
    // Première lecture (destructive). Applique aussitôt le supplément (page/décisions)
    // pour que la page rende le bon contenu ; le défilement suivra quand `ready`.
    if (pending.current === undefined) {
      const stash = readScrollStashOnce<E>(key);
      pending.current = stash;
      if (stash) {
        applyRef.current(stash.extra);
        justApplied.current = true; // ne pas poser le défilement sur CE passage
        setTick((n) => n + 1); // force un passage ultérieur (voir plus bas)
        return;
      }
    }

    const stash = pending.current;
    if (!stash) return;

    // Passage d'application : on laisse `apply` propager son changement d'état (et le
    // rechargement éventuel) ; on repassera quand `ready` reflétera la bonne page.
    if (justApplied.current) {
      justApplied.current = false;
      setTick((n) => n + 1); // force un nouveau passage post-application
      return;
    }

    if (!ready) return;

    // Données restaurées disposées → pose le défilement avant peinture (pas de saut),
    // puis marque la restauration terminée (ne se re-déclenche plus).
    if (scrollRef.current) scrollRef.current.scrollTop = stash.scrollTop;
    pending.current = null;
  }, [key, ready, scrollRef, tick]);
}
